import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Users, Search, Download, Filter, ExternalLink, 
  Wallet, CreditCard, TrendingUp, AlertTriangle, Eye, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerLedger = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchCustomers();
  }, [token, navigate]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API}/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
        setAccessDenied(false);
      } else if (response.status === 403) {
        setAccessDenied(true);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B';
    if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(2) + 'K';
    return n.toFixed(2);
  };

  const exportCSV = () => {
    const headers = ['Email', 'Name', 'Wallet', 'Created'];
    const rows = customers.map(c => [c.email, c.name, c.wallet_address || 'N/A', c.created_at]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Customer data exported');
  };

  const filteredCustomers = customers.filter(c => 
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.wallet_address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Card className="bg-gray-900/50 border-gray-800 max-w-md">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-gray-400 mb-4">Only platform administrators can access this page.</p>
            <Link to="/admin-dashboard">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="customer-ledger">
      {/* Header */}
      <div className="bg-gray-900/50 border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard">
              <Button variant="outline" size="sm" className="border-gray-700 text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <div className="h-8 w-px bg-gray-700" />
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Users className="w-6 h-6" />
                Customer Ledger
              </h1>
              <p className="text-gray-400 text-sm">View and manage all platform users</p>
            </div>
          </div>
          <Button onClick={exportCSV} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search by email, name, or wallet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-gray-900 border-gray-800 text-white"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-4">
              <p className="text-gray-400 text-sm">Total Users</p>
              <p className="text-2xl font-bold text-white">{customers.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-4">
              <p className="text-gray-400 text-sm">With Wallets</p>
              <p className="text-2xl font-bold text-white">
                {customers.filter(c => c.wallet_address).length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-4">
              <p className="text-gray-400 text-sm">Active Positions</p>
              <p className="text-2xl font-bold text-green-400">—</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-4">
              <p className="text-gray-400 text-sm">Total TVL</p>
              <p className="text-2xl font-bold text-blue-400">—</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer List */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Customers ({filteredCustomers.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-gray-400">Loading...</div>
                ) : filteredCustomers.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">No customers found</div>
                ) : (
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {filteredCustomers.map((customer, idx) => (
                      <div 
                        key={idx}
                        onClick={() => setSelectedCustomer(customer)}
                        className={`p-4 rounded-lg cursor-pointer transition-colors ${
                          selectedCustomer?.id === customer.id 
                            ? 'bg-[#10b981]/20 border border-[#10b981]/50' 
                            : 'bg-gray-800/50 hover:bg-gray-800'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">{customer.name || 'Unknown'}</p>
                            <p className="text-sm text-gray-400">{customer.email}</p>
                          </div>
                          <div className="text-right">
                            {customer.wallet_address ? (
                              <p className="text-xs text-gray-500 font-mono">
                                {customer.wallet_address.slice(0, 8)}...
                              </p>
                            ) : (
                              <span className="text-xs text-yellow-400">No wallet</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Customer Details */}
          <div>
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Customer Details</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedCustomer ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="text-white">{selectedCustomer.name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-white">{selectedCustomer.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Wallet</p>
                      {selectedCustomer.wallet_address ? (
                        <a 
                          href={`https://etherscan.io/address/${selectedCustomer.wallet_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 text-sm font-mono flex items-center gap-1"
                        >
                          {selectedCustomer.wallet_address.slice(0, 12)}...
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <p className="text-gray-500">Not connected</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Joined</p>
                      <p className="text-white text-sm">
                        {new Date(selectedCustomer.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    Select a customer to view details
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerLedger;
